import { Route, Redirect } from 'react-router-dom';
import {
  IonApp,
  IonRouterOutlet,
  setupIonicReact,
} from '@ionic/react';
import { IonReactRouter } from '@ionic/react-router';
import packageJson from '../package.json';
import Login from './pages/Login';
import CreateAccount from './pages/CreateAccount';
import Splash from './pages/SplashPage';
import Paywall from './pages/Paywall';
import Home from './pages/Home';
import Profile from './pages/Profile';
import Profile_Edit from './pages/Profile_Edit';
import Associates from './pages/Associates';
import Chats from './pages/Chats';
import CreateNewChat from './pages/CreateNewChat';
import AssociateProfile from './pages/AssociateProfile';
import ShiftDetails from "./pages/ShiftDetails";
import Chat from "./pages/Chat";
import SchedShiftDetail from "./pages/SchedShiftDetail";
import ScheduleJob from "./pages/ScheduleJob";
import SelectWerkers from "./pages/SelectWerkers";
import Announcement from "./pages/Announcement";
import AdminView from './pages/AdminView';
import Community from './pages/Community';
import CalendarDesktop from './pages/desktop/CalendarDesktop';
import CalendarMobile from './pages/CalendarMobile';
import LandingPage from './pages/LandingPage';
import AboutUsPage from './pages/AboutUsPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import PortalEntry from './pages/PortalEntry';
import TrainingPage from './pages/TrainingPage';
import { ProfileContext } from "../src/context/ProfileContext";
import SelectAnnouncementAudience from "./pages/SelectAnnouncementAudience";
import AnnouncementDetail from "./pages/AnnouncementDetail";
import Notifications from './pages/Notifications';
import PaymentsPage from './pages/payments/PaymentsPage';
import PaymentSummaryPage from './pages/payments/PaymentSummaryPage';import PaymentReceiptPage from "./pages/payments/PaymentReceiptPage";import ErrorBoundary from './components/ErrorBoundary';

/* Core CSS required for Ionic components to work properly */
import '@ionic/react/css/core.css';

/* Basic CSS for apps built with Ionic */
import '@ionic/react/css/normalize.css';
import '@ionic/react/css/structure.css';
import '@ionic/react/css/typography.css';

/* Optional CSS utils that can be commented out */
import '@ionic/react/css/padding.css';
import '@ionic/react/css/float-elements.css';
import '@ionic/react/css/text-alignment.css';
import '@ionic/react/css/text-transformation.css';
import '@ionic/react/css/flex-utils.css';
import '@ionic/react/css/display.css';

/* Theme variables */
import './theme/variables.css';
import ForceUpdate from './pages/ForceUpdate';
import { useContext, useEffect } from 'react';
import SelectWerkersSeries from './pages/selectWerkersSeries';

setupIonicReact();

const App: React.FC = () => {
  const { forceUpdate } = useContext(ProfileContext);
  const isWebsiteMode = packageJson.appwebsite === 'website';

  useEffect(() => {
    const getDeepActiveElement = (): HTMLElement | null => {
      let activeElement: Element | null = document.activeElement;

      while (activeElement && (activeElement as HTMLElement).shadowRoot?.activeElement) {
        activeElement = (activeElement as HTMLElement).shadowRoot?.activeElement || null;
      }

      return activeElement instanceof HTMLElement ? activeElement : null;
    };

    const blurActiveElement = () => {
      const deepActiveElement = getDeepActiveElement();
      if (!deepActiveElement) {
        return;
      }

      if (deepActiveElement !== document.body) {
        deepActiveElement.blur();
      }

      const hostElement = document.activeElement;
      if (hostElement instanceof HTMLElement && hostElement !== document.body) {
        hostElement.blur();
      }
    };

    const blurIfFocusedInsideAriaHidden = () => {
      const deepActiveElement = getDeepActiveElement();
      if (!deepActiveElement || deepActiveElement === document.body) {
        return;
      }

      const hiddenAncestor = deepActiveElement.closest('[aria-hidden="true"]');
      if (!hiddenAncestor) {
        return;
      }

      deepActiveElement.blur();

      const hostElement = document.activeElement;
      if (hostElement instanceof HTMLElement && hostElement !== document.body) {
        hostElement.blur();
      }
    };

    window.addEventListener('ionRouteWillChange', blurActiveElement as EventListener);
    window.addEventListener('ionRouteDidChange', blurIfFocusedInsideAriaHidden as EventListener);
    document.addEventListener('focusin', blurIfFocusedInsideAriaHidden, true);

    return () => {
      window.removeEventListener('ionRouteWillChange', blurActiveElement as EventListener);
      window.removeEventListener('ionRouteDidChange', blurIfFocusedInsideAriaHidden as EventListener);
      document.removeEventListener('focusin', blurIfFocusedInsideAriaHidden, true);
    };
  }, []);
  
  return (
    <IonApp>
      <IonReactRouter>
        <ErrorBoundary inline>
        <IonRouterOutlet>
          <Route exact path="/Home">
            {forceUpdate === false ? <Home /> : <ForceUpdate />}
          </Route>
          <Route exact path="/Profile">
            {forceUpdate === false ? <Profile /> : <ForceUpdate />}
          </Route>
          <Route exact path="/Profile_Edit">
            {/* eslint-disable-next-line react/jsx-pascal-case */}
            <Profile_Edit />
          </Route>
          <Route exact path="/Associates">
            {forceUpdate === false ? <Associates /> : <ForceUpdate />}
          </Route>
          <Route exact path="/Community">
            {forceUpdate === false ? <Community /> : <Community />}
          </Route>
          <Route exact path="/Chats">
            {forceUpdate === false ? <Chats /> : <ForceUpdate />}
          </Route>
          <Route exact path="/Calendar">
            {forceUpdate === false ? <CalendarDesktop /> : <ForceUpdate />}
          </Route>
          <Route exact path="/SchedulerCalendar">
            {forceUpdate === false ? <CalendarMobile /> : <ForceUpdate />}
          </Route>
          <Route exact path="/notifications">
            {forceUpdate === false ? <Notifications /> : <ForceUpdate />}
          </Route>
          <Route exact path="/CreateNewChat">
            <CreateNewChat />
          </Route>
          <Route path="/AssociateProfile/:id" component={AssociateProfile} />
          <Route path="/Chat/:id" component={Chat} />
          <Route path="/ShiftDetails/:jobDocId/:uniqueJobDocId/:schedUid" component={ShiftDetails} />
          <Route path="/SchedShiftDetail/:id" component={SchedShiftDetail} />
          <Route exact path="/ScheduleJob"><ScheduleJob />
          </Route>
          <Route path="/SelectWerkersSeries/:seriesId" component={SelectWerkersSeries} exact />
          <Route path="/SelectAnnouncementAudience" component={SelectAnnouncementAudience} />
          <Route path="/SelectWerkers/:id" component={SelectWerkers} />
          <Route path="/Announcement" component={Announcement} />
          <Route path="/AnnouncementDetail/:id" component={AnnouncementDetail} />
          <Route exact path="/payments">
            {forceUpdate === false ? <PaymentsPage /> : <ForceUpdate />}
          </Route>
          <Route exact path="/payments/summary">
            {forceUpdate === false ? <PaymentSummaryPage /> : <ForceUpdate />}
          </Route>
          <Route exact path="/payments/receipt/:receiptId">
            {forceUpdate === false ? <PaymentReceiptPage /> : <ForceUpdate />}
          </Route>
          <Route exact path="/notifications/receipt/:schedulerUid/:receiptId">
            {forceUpdate === false ? <PaymentReceiptPage /> : <ForceUpdate />}
          </Route>
        </IonRouterOutlet>  
        </ErrorBoundary>
        <ErrorBoundary inline>
        <Route exact path="/" render={() => isWebsiteMode ? <LandingPage /> : <Redirect to="/Splash" />} />
        <Route path="/about" component={AboutUsPage} />
        <Route path="/privacy" component={PrivacyPolicyPage} />
        <Route path="/portal" component={PortalEntry} />
        <Route path="/training" component={TrainingPage} />
        <Route path="/login" component={Login} />
        <Route path="/createAccount" component={CreateAccount} />
        <Route path="/Splash" component={Splash} />
        <Route path="/Paywall" component={Paywall} />
        <Route path="/AdminView" component={AdminView} />
        <Route path="/forceUpdate" component={ForceUpdate} />
        </ErrorBoundary>
      </IonReactRouter>
    </IonApp>
  );
};

export default App;